import type { CoreAccount, CoreHousehold, SampleLiability, SampleNetWorth } from '../types/financial';
import { calculateMonthlyPayment } from './loanAmortization';
import { HOME_LOAN_PLANNER_ID } from './loanPlannerSeed';

type LoanLiabilitySeed = {
  loanId?: string;
  principal: number;
  interestRatePct: number;
  termYears: number;
  label?: string;
};

/**
 * Upsert the home-loan liability on the household balance sheet so net worth
 * and Investments vs debt always include the planner / backend principal.
 * Returns the same core reference when nothing changed (avoids update loops).
 */
export function applyHomeLoanToHousehold(
  core: CoreHousehold,
  seed: LoanLiabilitySeed,
): CoreHousehold {
  if (!(seed.principal > 0)) return core;

  const payment = calculateMonthlyPayment(
    seed.principal,
    seed.interestRatePct,
    Math.max(1, Math.round(seed.termYears * 12)),
  );
  const loanId = seed.loanId || HOME_LOAN_PLANNER_ID;
  const mortgageIndex = core.accounts.findIndex(
    (a) => a.kind === 'liability' && (a.type === 'mortgage' || a.id === loanId),
  );

  if (mortgageIndex >= 0) {
    const existing = core.accounts[mortgageIndex]!;
    if (
      existing.balance === seed.principal &&
      existing.interestRatePct === seed.interestRatePct &&
      existing.monthlyPayment === payment &&
      (seed.label == null || existing.label === seed.label)
    ) {
      return core;
    }
    const accounts = [...core.accounts];
    accounts[mortgageIndex] = {
      ...existing,
      balance: seed.principal,
      interestRatePct: seed.interestRatePct,
      monthlyPayment: payment,
      label: seed.label || existing.label || 'Home loan',
      type: 'mortgage',
    };
    return { ...core, accounts };
  }

  const nextLiability: CoreAccount = {
    id: loanId === 'default' ? HOME_LOAN_PLANNER_ID : loanId,
    kind: 'liability',
    type: 'mortgage',
    label: seed.label || 'Home loan',
    balance: seed.principal,
    interestRatePct: seed.interestRatePct,
    monthlyPayment: payment,
  };

  return { ...core, accounts: [...core.accounts, nextLiability] };
}

function sameLiability(a: SampleLiability, b: SampleLiability): boolean {
  return (
    a.id === b.id ||
    (a.type === b.type && a.label.trim().toLowerCase() === b.label.trim().toLowerCase())
  );
}

/**
 * Keep Home / Wealth "Liabilities" aligned with the loan planner widget.
 * Draft overlays used to drop bundled `liab_*` sample loans, so the strip
 * showed $0 while Home loan still had a principal.
 */
export function mergeHomeLoanIntoNetWorth(
  netWorth: SampleNetWorth,
  homeLoan: SampleLiability | null | undefined,
): SampleNetWorth {
  if (!homeLoan || !(homeLoan.balance > 0)) return netWorth;

  const existingIndex = netWorth.liabilities.findIndex((row) => sameLiability(row, homeLoan));
  let liabilities: SampleLiability[];
  if (existingIndex >= 0) {
    const current = netWorth.liabilities[existingIndex]!;
    if (current.balance === homeLoan.balance) return netWorth;
    liabilities = [...netWorth.liabilities];
    liabilities[existingIndex] = { ...current, ...homeLoan };
  } else {
    liabilities = [...netWorth.liabilities, homeLoan];
  }

  const totalLiabilities = liabilities.reduce((sum, row) => sum + row.balance, 0);
  return {
    ...netWorth,
    liabilities,
    totalLiabilities,
    total: netWorth.activeInvestments + netWorth.passiveInvestments - totalLiabilities,
  };
}
